import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import PharmeasyLayout from "../../components/PharmeasyLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { RefreshCw, Loader2, RotateCcw, Download, ChevronLeft, ChevronRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const PAGE_SIZE = 20;

const fmt = (num) => (num == null ? "0" : num.toLocaleString("en-IN"));

const downloadCSV = (headers, rows, filename) => {
  const csv = [headers, ...rows].map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const exportMainCSV = (data) => {
  if (!data.length) return;
  const headers = ["Date", "Month", "Orders (EDD)", "Forward (EDD)", "Return (EDD)", "Delivered (Same EDD)", "Fwd Delivered (Same EDD)", "Ret Delivered (Same EDD)", "Delivered (On Date)", "Fwd Delivered (On Date)", "Ret Delivered (On Date)", "Riders Used", "Total KM (Google)", "EdgeLMS Riders", "Rider Capacity Assumption", "EdgeLMS KM"];
  const rows = data.map((r) => [
    r.date, r.month, r.total_orders_with_edd, r.forward_orders_edd || 0, r.return_orders_edd || 0,
    r.total_delivered_same_edd, r.forward_delivered_same_edd || 0, r.return_delivered_same_edd || 0,
    r.total_delivered_on_date, r.forward_delivered_on_date || 0, r.return_delivered_on_date || 0,
    r.total_rider_count_used,
    r.total_kms_google?.toFixed(1) || "0", r.edgelms_rider_count,
    r.rider_capacity_used || "",
    r.edgelms_total_rider_km?.toFixed(1) || "0",
  ]);
  downloadCSV(headers, rows, `revenue_projection_${new Date().toISOString().slice(0, 10)}.csv`);
};

const exportRiderKmCSV = (riderData, date, label) => {
  if (!riderData?.length) return;
  const headers = ["Rider", "KM", ...(riderData[0].shipment_count != null ? ["Shipments"] : [])];
  const rows = riderData.map((r) => {
    const row = [r.rider, r.km?.toFixed(1) || "0"];
    if (r.shipment_count != null) row.push(r.shipment_count);
    return row;
  });
  downloadCSV(headers, rows, `${label}_rider_km_${date}.csv`);
};

export default function PharmeasyRevenueProjection({ user }) {
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [hoveredKm, setHoveredKm] = useState(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/pharmeasy-orders/revenue-projections`);
      setAllData(res.data?.data || []);
    } catch {
      toast.error("Failed to load projections");
    } finally {
      setLoading(false);
    }
  };

  const triggerBackfill = async () => {
    setBackfillLoading(true);
    try {
      await axios.post(`${API}/pharmeasy-orders/revenue-projections/backfill`);
      toast.success("Backfill started in background. Refresh in a few minutes.");
    } catch {
      toast.error("Failed to start backfill");
    } finally {
      setBackfillLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // Filter by date range
  const filtered = useMemo(() => {
    let d = allData;
    if (dateFrom) d = d.filter((r) => r.date >= dateFrom);
    if (dateTo) d = d.filter((r) => r.date <= dateTo);
    return d;
  }, [allData, dateFrom, dateTo]);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [dateFrom, dateTo]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageData = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Summary stats based on filtered data
  const stats = useMemo(() => {
    const n = filtered.length;
    if (n === 0) return null;
    const totalOrders = filtered.reduce((s, r) => s + (r.total_orders_with_edd || 0), 0);
    const totalRiders = filtered.reduce((s, r) => s + (r.total_rider_count_used || 0), 0);
    const totalKmGoogle = filtered.reduce((s, r) => s + (r.total_kms_google || 0), 0);
    const totalEdgRiders = filtered.reduce((s, r) => s + (r.edgelms_rider_count || 0), 0);
    const totalEdgKm = filtered.reduce((s, r) => s + (r.edgelms_total_rider_km || 0), 0);
    return {
      totalDays: n,
      avgOrders: (totalOrders / n).toFixed(1),
      avgRiders: Math.ceil(totalRiders / n),
      avgKmActual: (totalKmGoogle / n).toFixed(1),
      avgRidersPlanned: Math.ceil(totalEdgRiders / n),
      avgKmPlanned: (totalEdgKm / n).toFixed(1),
    };
  }, [filtered]);

  return (
    <PharmeasyLayout user={user} activeTab="revenue">
      <div className="space-y-6">
        <Card className="border-0 shadow-lg">
          <CardHeader className="pb-3">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <CardTitle className="text-lg font-semibold" data-testid="revenue-projection-title">
                Revenue Projection
              </CardTitle>
              <div className="flex flex-wrap gap-3">
                <Button variant="outline" size="sm" onClick={fetchData} disabled={loading} data-testid="refresh-projections-btn">
                  <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
                </Button>
                <Button variant="outline" size="sm" onClick={() => exportMainCSV(filtered)} disabled={!filtered.length} data-testid="export-projections-btn">
                  <Download className="w-4 h-4 mr-1" /> Export
                </Button>
                <Button variant="outline" size="sm" onClick={triggerBackfill} disabled={backfillLoading} data-testid="backfill-projections-btn">
                  {backfillLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-1" />} Backfill All
                </Button>
              </div>
            </div>
            {/* Date range filters */}
            <div className="flex flex-wrap items-center gap-4 mt-3">
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 font-medium">From</span>
                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40 h-8 text-sm" data-testid="date-from" />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 font-medium">To</span>
                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40 h-8 text-sm" data-testid="date-to" />
              </div>
              {(dateFrom || dateTo) && (
                <Button variant="ghost" size="sm" onClick={() => { setDateFrom(""); setDateTo(""); }} className="text-xs h-8">
                  Clear
                </Button>
              )}
              <span className="text-xs text-gray-400 ml-auto">{filtered.length} records</span>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {/* Summary Stats */}
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
                <div className="bg-slate-50 rounded-lg p-4 text-center">
                  <p className="text-xs text-gray-500 font-medium">Total Days</p>
                  <p className="text-xl font-bold text-gray-800">{stats.totalDays}</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <p className="text-xs text-gray-500 font-medium">Avg Orders / Day</p>
                  <p className="text-xl font-bold text-blue-700">{stats.avgOrders}</p>
                </div>
                <div className="bg-emerald-50 rounded-lg p-4 text-center">
                  <p className="text-xs text-gray-500 font-medium">Avg Riders Used</p>
                  <p className="text-xl font-bold text-emerald-700">{stats.avgRiders}</p>
                </div>
                <div className="bg-orange-50 rounded-lg p-4 text-center">
                  <p className="text-xs text-gray-500 font-medium">Avg KM / Day</p>
                  <p className="text-xs text-gray-400 -mt-0.5 mb-0.5">(Actual)</p>
                  <p className="text-xl font-bold text-orange-700">{stats.avgKmActual}</p>
                </div>
                <div className="bg-indigo-50 rounded-lg p-4 text-center">
                  <p className="text-xs text-gray-500 font-medium">Avg Riders Required</p>
                  <p className="text-xs text-gray-400 -mt-0.5 mb-0.5">(If Planned)</p>
                  <p className="text-xl font-bold text-indigo-700">{stats.avgRidersPlanned}</p>
                </div>
                <div className="bg-violet-50 rounded-lg p-4 text-center">
                  <p className="text-xs text-gray-500 font-medium">Avg KM / Day</p>
                  <p className="text-xs text-gray-400 -mt-0.5 mb-0.5">(If Planned)</p>
                  <p className="text-xl font-bold text-violet-700">{stats.avgKmPlanned}</p>
                </div>
              </div>
            )}
            {loading && !allData.length ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-16 text-gray-500 text-sm">
                {allData.length === 0 ? 'No projection data yet. Click "Backfill All" to generate.' : "No data in selected date range."}
              </div>
            ) : (
              <>
                <div className="overflow-x-auto" data-testid="revenue-projection-table">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-slate-50">
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Month</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Orders<br/><span className="font-normal text-xs text-gray-500">(EDD)</span></th>
                        <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Forward<br/><span className="font-normal text-xs text-gray-500">(EDD)</span></th>
                        <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Return<br/><span className="font-normal text-xs text-gray-500">(EDD)</span></th>
                        <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Delivered<br/><span className="font-normal text-xs text-gray-500">(Same EDD)</span></th>
                        <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Fwd Delivered<br/><span className="font-normal text-xs text-gray-500">(Same EDD)</span></th>
                        <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Ret Delivered<br/><span className="font-normal text-xs text-gray-500">(Same EDD)</span></th>
                        <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Delivered<br/><span className="font-normal text-xs text-gray-500">(On Date)</span></th>
                        <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Fwd Delivered<br/><span className="font-normal text-xs text-gray-500">(On Date)</span></th>
                        <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Ret Delivered<br/><span className="font-normal text-xs text-gray-500">(On Date)</span></th>
                        <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Riders<br/><span className="font-normal text-xs text-gray-500">Used</span></th>
                        <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Total KM<br/><span className="font-normal text-xs text-gray-500">(Google)</span></th>
                        <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">EdgeLMS<br/><span className="font-normal text-xs text-gray-500">Riders</span></th>
                        <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Rider Capacity<br/><span className="font-normal text-xs text-gray-500">Assumption</span></th>
                        <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">EdgeLMS<br/><span className="font-normal text-xs text-gray-500">KM</span></th>
                      </tr>
                    </thead>
                    <tbody>
                      {pageData.map((row, idx) => (
                        <tr key={row.date} className={`border-b ${idx % 2 === 0 ? "bg-white" : "bg-slate-50/50"} hover:bg-blue-50/40 transition-colors`}>
                          <td className="px-4 py-3 align-middle font-medium text-gray-900 whitespace-nowrap">{row.date}</td>
                          <td className="px-4 py-3 align-middle text-gray-600 whitespace-nowrap">{row.month}</td>
                          <td className="px-4 py-3 align-middle text-right font-medium text-blue-700">{fmt(row.total_orders_with_edd)}</td>
                          <td className="px-4 py-3 align-middle text-right text-blue-600">{fmt(row.forward_orders_edd)}</td>
                          <td className="px-4 py-3 align-middle text-right text-blue-500">{fmt(row.return_orders_edd)}</td>
                          <td className="px-4 py-3 align-middle text-right font-medium text-emerald-700">{fmt(row.total_delivered_same_edd)}</td>
                          <td className="px-4 py-3 align-middle text-right text-emerald-600">{fmt(row.forward_delivered_same_edd)}</td>
                          <td className="px-4 py-3 align-middle text-right text-emerald-500">{fmt(row.return_delivered_same_edd)}</td>
                          <td className="px-4 py-3 align-middle text-right font-medium text-emerald-600">{fmt(row.total_delivered_on_date)}</td>
                          <td className="px-4 py-3 align-middle text-right text-emerald-500">{fmt(row.forward_delivered_on_date)}</td>
                          <td className="px-4 py-3 align-middle text-right text-emerald-400">{fmt(row.return_delivered_on_date)}</td>
                          <td className="px-4 py-3 align-middle text-right">{row.total_rider_count_used}</td>
                          {/* Google KM */}
                          <KmCell
                            value={row.total_kms_google}
                            riderData={row.rider_wise_kms_google}
                            hovered={hoveredKm?.type === "google" && hoveredKm?.date === row.date}
                            onEnter={() => setHoveredKm({ type: "google", date: row.date })}
                            onLeave={() => setHoveredKm(null)}
                            onExport={() => exportRiderKmCSV(row.rider_wise_kms_google, row.date, "google")}
                            label="Rider Wise KM (Google)"
                            color="orange"
                          />
                          <td className="px-4 py-3 align-middle text-right">
                            <Badge variant="outline" className="text-xs">{row.edgelms_rider_count}</Badge>
                          </td>
                          <td className="px-4 py-3 align-middle text-right text-gray-600">
                            {row.rider_capacity_used || "-"}
                          </td>
                          {/* EdgeLMS KM */}
                          <KmCell
                            value={row.edgelms_total_rider_km}
                            riderData={row.edgelms_rider_wise_km}
                            hovered={hoveredKm?.type === "edg" && hoveredKm?.date === row.date}
                            onEnter={() => setHoveredKm({ type: "edg", date: row.date })}
                            onLeave={() => setHoveredKm(null)}
                            onExport={() => exportRiderKmCSV(row.edgelms_rider_wise_km, row.date, "edgelms")}
                            label="Rider Wise KM (EdgeLMS Plan)"
                            color="indigo"
                            showCount
                          />
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Pagination */}
                <div className="flex items-center justify-between mt-4 px-1">
                  <span className="text-xs text-gray-500">
                    Page {page} of {totalPages} ({filtered.length} total)
                  </span>
                  <div className="flex gap-1">
                    <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="prev-page-btn">
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                      let p;
                      if (totalPages <= 5) p = i + 1;
                      else if (page <= 3) p = i + 1;
                      else if (page >= totalPages - 2) p = totalPages - 4 + i;
                      else p = page - 2 + i;
                      return (
                        <Button key={p} variant={p === page ? "default" : "outline"} size="sm" onClick={() => setPage(p)} className="w-8 h-8 p-0 text-xs">
                          {p}
                        </Button>
                      );
                    })}
                    <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="next-page-btn">
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </PharmeasyLayout>
  );
}

/* Reusable KM cell with hover tooltip + export button */
function KmCell({ value, riderData, hovered, onEnter, onLeave, onExport, label, color, showCount }) {
  const textColor = `text-${color}-700`;
  return (
    <td
      className={`px-4 py-3 align-middle text-right font-medium ${textColor} cursor-pointer relative`}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
    >
      {value?.toFixed(1) || "0"} km
      {hovered && riderData?.length > 0 && (
        <div
          className="absolute z-50 right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl p-3 min-w-[280px] text-left"
          onMouseEnter={onEnter}
          onMouseLeave={onLeave}
        >
          <div className="flex items-center justify-between mb-2 border-b pb-1">
            <p className="font-semibold text-gray-800 text-xs">{label}</p>
            <button
              onClick={(e) => { e.stopPropagation(); onExport(); }}
              className="flex items-center gap-1 text-[10px] text-blue-600 hover:text-blue-800 font-medium px-1.5 py-0.5 rounded hover:bg-blue-50 transition-colors"
              data-testid="export-rider-km-btn"
            >
              <Download className="w-3 h-3" /> Export
            </button>
          </div>
          {riderData.map((r, i) => (
            <div key={i} className="flex justify-between text-xs py-0.5">
              <span className="text-gray-600 truncate mr-2" style={{ maxWidth: 170 }}>
                {r.rider}{showCount && r.shipment_count != null ? ` (${r.shipment_count})` : ""}
              </span>
              <span className={`font-medium ${textColor} whitespace-nowrap`}>{r.km?.toFixed(1)} km</span>
            </div>
          ))}
          <div className="flex justify-between text-xs py-1 mt-1 border-t font-semibold">
            <span className="text-gray-700">Total</span>
            <span className={textColor}>{value?.toFixed(1)} km</span>
          </div>
        </div>
      )}
    </td>
  );
}
